import React from "react";
import { AmountCell2 } from "@/modules/work/billables/cells";
import CustomList from "components/list/custom";
import { Link } from "react-router-dom";
import { createFilteredUrl } from "@/hooks/useUrlState";
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from "date-fns";
import { formatDateForAPI } from "utils/utils";

export function RevenueGrowth() {
  const today = new Date();

  // Start of the month that is 12 months ago
  const startDate = startOfMonth(subMonths(today, 11));

  // Last day of the month that is 3 months from now
  const endDate = endOfMonth(addMonths(today, 3));

  const dateFilters = {
    startDate: formatDateForAPI(startDate),
    endDate: formatDateForAPI(endDate),
  };

  const columns = [
    {
      accessorKey: "collectedYearMonth",
      header: "Month",
      cell: ({ getValue }) => {
        const date = new Date(getValue());
        return format(date, "MMM yyyy");
      },
    },
    {
      accessorKey: "dedicated",
      header: "Dedicated",
      cell: ({ getValue, row }) => {
        const [year, monthIndex] = row.original.collectedYearMonth.split("-");
        const monthStart = new Date(year, parseInt(monthIndex) - 1, 1);
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthEnd.getMonth() + 1, 0);

        const baseContractFilters = {
          contacts: {
            projects: {
              jobs: {
                type: { _eq: "dedicated" },

                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  billing_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    type: { _eq: "dedicated" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    type: { _eq: "dedicated" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    type: { _eq: "dedicated" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      type: { _eq: "dedicated" },
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        const baseBilledFilters = {
          contacts: {
            projects: {
              jobs: {
                type: { _eq: "dedicated" },

                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  invoice_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    type: { _eq: "dedicated" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    type: { _eq: "dedicated" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    type: { _eq: "dedicated" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      type: { _eq: "dedicated" },
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        console.log("base contract filter", baseContractFilters);


        return (
          <div>
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseContractFilters,
              )}

              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().contracted} />
            </Link>{" "}
            |{" "}
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseBilledFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().billed} />
            </Link>
          </div>
        );
      },
    },
    {
      accessorKey: "fixed",
      header: "Fixed",
      cell: ({ getValue, row }) => {
        const [year, monthIndex] = row.original.collectedYearMonth.split("-");
        const monthStart = new Date(year, parseInt(monthIndex) - 1, 1);
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthEnd.getMonth() + 1, 0); // Set to the last day of the month

        // Map to All Billables filters schema
        const baseContractFilters = {
          contacts: {
            projects: {
              jobs: {
                type: { _eq: "fixed" },
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  billing_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    type: { _eq: "fixed" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    type: { _eq: "fixed" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    type: { _eq: "fixed" },
                    billables: {
                      type: { _eq: "fixed" },
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      type: { _eq: "fixed" },
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        const baseBilledFilters = {
          contacts: {
            projects: {
              jobs: {
                type: { _eq: "fixed" },
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  invoice_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    type: { _eq: "fixed" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    type: { _eq: "fixed" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    type: { _eq: "fixed" },
                    billables: {
                      type: { _eq: "fixed" },
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      type: { _eq: "fixed" },
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };
        return (
          <div>
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseContractFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().contracted} />
            </Link>{" "}
            |{" "}
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseBilledFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().billed} />
            </Link>
          </div>
        );
      },
    },
    {
      accessorKey: "hourly",
      header: "Hourly",
      cell: ({ getValue, row }) => {
        const [year, monthIndex] = row.original.collectedYearMonth.split("-");
        const monthStart = new Date(year, parseInt(monthIndex) - 1, 1);
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthEnd.getMonth() + 1, 0); // Set to the last day of the month

        // Map to All Billables filters schema
        const baseContractFilters = {
          contacts: {
            projects: {
              jobs: {
                type: { _eq: "hourly" },
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  billing_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    type: { _eq: "hourly" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    type: { _eq: "hourly" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    type: { _eq: "hourly" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      type: { _eq: "hourly" },
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        const baseBilledFilters = {
          contacts: {
            projects: {
              jobs: {
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  invoice_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        return (
          <div>
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseContractFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().contracted} />
            </Link>{" "}
            |{" "}
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseBilledFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().billed} />
            </Link>
          </div>
        );
      },
    },
    {
      accessorKey: "bucket",
      header: "Bucket",
      cell: ({ getValue, row }) => {
        const [year, monthIndex] = row.original.collectedYearMonth.split("-");
        const monthStart = new Date(year, parseInt(monthIndex) - 1, 1);
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthEnd.getMonth() + 1, 0); // Set to the last day of the month

        // Map to All Billables filters schema
        const baseContractFilters = {
          contacts: {
            projects: {
              jobs: {
                type: { _eq: "bucket" },
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  billing_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    type: { _eq: "bucket" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    type: { _eq: "bucket" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    type: { _eq: "bucket" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      type: { _eq: "bucket" },
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        const baseBilledFilters = {
          contacts: {
            projects: {
              jobs: {
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  invoice_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        return (
          <div>
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseContractFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().contracted} />
            </Link>{" "}
            |{" "}
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseBilledFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().billed} />
            </Link>
          </div>
        );
      },
    },
    {
      accessorKey: "recurring",
      header: "Recurring",
      cell: ({ getValue, row }) => {
        const [year, monthIndex] = row.original.collectedYearMonth.split("-");
        const monthStart = new Date(year, parseInt(monthIndex) - 1, 1);
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthEnd.getMonth() + 1, 0); // Set to the last day of the month

        // Map to All Billables filters schema
        const baseContractFilters = {
          contacts: {
            projects: {
              jobs: {
                type: { _eq: "recurring" },
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  billing_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    type: { _eq: "recurring" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    type: { _eq: "recurring" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    type: { _eq: "recurring" },
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      type: { _eq: "recurring" },
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        const baseBilledFilters = {
          contacts: {
            projects: {
              jobs: {
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  invoice_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        return (
          <div>
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseContractFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().contracted} />
            </Link>{" "}
            |{" "}
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseBilledFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().billed} />
            </Link>
          </div>
        );
      },
    },
    {
      accessorKey: "totals",
      header: "Total Revenue",
      cell: ({ getValue, row }) => {
        const [year, monthIndex] = row.original.collectedYearMonth.split("-");
        const monthStart = new Date(year, parseInt(monthIndex) - 1, 1);
        const monthEnd = new Date(monthStart);
        monthEnd.setMonth(monthEnd.getMonth() + 1, 0);

        const baseContractFilters = {
          contacts: {
            projects: {
              jobs: {
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  billing_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      status: { _nin: ["cancelled", "archived"] },
                      billing_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };
        const baseBilledFilters = {
          contacts: {
            projects: {
              jobs: {
                billables: {
                  status: { _nin: ["cancelled", "archived"] },
                  invoice_date: {
                    _gte: formatDateForAPI(monthStart),
                    _lte: formatDateForAPI(monthEnd),
                  },
                },
              },
            },
          },
          deep: {
            contacts: {
              _filter: {
                projects: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
              projects: {
                _filter: {
                  jobs: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
                jobs: {
                  _filter: {
                    billables: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                  billables: {
                    _filter: {
                      status: { _nin: ["cancelled", "archived"] },
                      invoice_date: {
                        _gte: formatDateForAPI(monthStart),
                        _lte: formatDateForAPI(monthEnd),
                      },
                    },
                  },
                },
              },
            },
          },
        };

        return (
          <div>
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseContractFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().contracted} />
            </Link>{" "}
            |{" "}
            <Link
              className="hover:underline"
              to={createFilteredUrl(
                "/reports/billables/status/all",
                baseBilledFilters,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AmountCell2 getValue={() => getValue().billed} />
            </Link>
          </div>
        );
      },
    },
  ];

  return (
    <CustomList
      title="Revenue Growth ( Contracted | Billed )"
      info="Monthly breakdown of revenue by type showing growth trends across dedicated, fixed, and hourly billing models."
      endPoint="custom-ui/mom-growth"
      columns={columns}
      filters={dateFilters}
      persistFilters={false}
      hidePagination={true}
    />
  );
}
